/**
 * RESPONSIBILITY: draw the mark a social row asks for.
 *
 * The row names it — see FooterLink. A row naming a mark nobody has drawn yet falls
 * back to the first letter of its own label, so it still reads as something.
 *
 * IN THEIR OWN COLOURS. YouTube's red, Facebook's blue, Instagram's gradient are part of
 * each mark, not part of the church's palette — which is why they are literals here, in
 * the drawing, and not theme tokens in the stylesheet. A visitor scanning a footer finds
 * these by colour before shape; drawn in the site's own ink they were three circles that
 * looked like decoration.
 */
import { useId } from 'react';

function YouTube() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="1" y="4.5" width="22" height="15" rx="4.5" fill="#FF0000" />
      <path d="M10 8.9v6.2l5.4-3.1z" fill="#FFFFFF" />
    </svg>
  );
}

function Facebook() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="11" fill="#1877F2" />
      <path
        d="M13.4 22.9v-8.1h2.6l.4-3h-3V9.9c0-.9.3-1.5 1.5-1.5h1.6V5.7c-.3 0-1.2-.1-2.3-.1-2.3 0-3.9 1.4-3.9 4v2.2H7.7v3h2.6v7.8"
        fill="#FFFFFF"
      />
    </svg>
  );
}

function Instagram() {
  // Unique per drawing: two instances on one page must not share a gradient id, or the
  // second one paints with whichever the browser found first.
  const id = `ig-${useId().replace(/:/g, '')}`;
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#FEDA75" />
          <stop offset=".3" stopColor="#FA7E1E" />
          <stop offset=".55" stopColor="#D62976" />
          <stop offset=".8" stopColor="#962FBF" />
          <stop offset="1" stopColor="#4F5BD5" />
        </linearGradient>
      </defs>
      <rect x="1.5" y="1.5" width="21" height="21" rx="6" fill={`url(#${id})`} />
      <rect x="6.3" y="6.3" width="11.4" height="11.4" rx="3.6" fill="none" stroke="#FFFFFF" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="2.8" fill="none" stroke="#FFFFFF" strokeWidth="1.8" />
      <circle cx="15.7" cy="8.3" r=".95" fill="#FFFFFF" />
    </svg>
  );
}

const MARKS: Record<string, () => React.ReactElement> = {
  youtube: YouTube,
  facebook: Facebook,
  instagram: Instagram,
};

export function SocialIcon({ name, fallback }: { name: string | undefined; fallback: string }) {
  const Mark = MARKS[name?.trim().toLowerCase() ?? ''];
  return Mark ? <Mark /> : <>{fallback}</>;
}
